// #2749: the Web's list of tasks, judgments and waits. The rows come from
// GET /api/work-list (docs/architecture/2026-10-18-work-list-readback.md);
// this module only words them. It decides no kind, priority or deadline.

import { serviceLabel } from './ai-work-cards-logic';

export type WorkKind = 'task' | 'judgment' | 'waiting';
export type WorkGroup = 'confirm' | 'choose' | 'result' | 'next';
/** やること ('mine') shows the Goals and the judgments together. */
export type WorkFilter = WorkKind | 'mine' | null;
type Lang = 'ja' | 'en';

export interface WorkNote {
  code: string;
  params: Record<string, unknown>;
}

export interface WorkStep {
  subtask_id: string;
  title: string;
  checkable: boolean;
  closed: { reason: 'done' | 'not_needed'; from: string | null; sent_by_principal: boolean; basis?: 'condition_met' | 'time_passed' } | null;
  /** The AI's steps sit in the Goal's card beside the user's (2026-10-20). */
  executor?: 'human' | 'ai';
  /** #3012: on an AI step the AI has started. */
  in_progress?: WorkInProgress | null;
}

/** #3012: 「AIが進行中」 and which AI started it (null name when not known). */
export interface WorkInProgress {
  client_id: string | null;
  client_name: string | null;
  since: string | null;
}

/** #3012: 「AI」, or 「AIが進行中（Dots）」 once the AI has started the task. */
export function aiWhoText(inProgress: WorkInProgress | null | undefined, lang: 'ja' | 'en'): string {
  if (!inProgress) return 'AI';
  const name = inProgress.client_name;
  if (lang === 'en') return name ? `AI in progress (${name})` : 'AI in progress';
  return name ? `AIが進行中（${name}）` : 'AIが進行中';
}

/** Which rows the list shows (#2856): due by today (overdue included), or any date. */
export type WorkRange = 'today' | 'all';

/** Where the AI does a piece of work (`metadata.ai_work_route`, #2732). */
export interface WorkAiRoute {
  how: string;
  where: string;
  tool_service: string;
  client_name: string | null;
}

export interface WorkItem {
  key: string;
  kind: WorkKind;
  group: WorkGroup | null;
  goal_id: string;
  goal_title: string;
  title: string;
  subtask_id: string | null;
  executor: 'human' | 'ai' | null;
  /** On an AI row: where the AI works, when the task names it (#2857). */
  ai_route?: WorkAiRoute | null;
  in_progress?: WorkInProgress | null;
  checkable: boolean;
  goal_check: boolean;
  /** `provisional`: no deadline was read; the principal's default applies (#3064, shown with 「仮」). */
  due: { day: string; time: string | null; provisional?: boolean } | null;
  overdue: boolean;
  priority: 'high' | 'low';
  priority_reason: 'overdue' | 'due_soon' | 'urgent' | null;
  /** #3184: why now, each reason with its record; the list words only the urgency here. */
  why_now?: WorkWhyNowReason[];
  note: WorkNote | null;
  options: Array<{ key: string; value: string; label: string }> | null;
  /** #2951: a place card with no place written: a box to type it in, sent with this option. */
  text_input?: { key: string; value: string; placeholder: string } | null;
  selection_token: string | null;
  waiting: { on: 'person' | 'ai'; since: string | null } | null;
  /** #2901: on a card, what the plan waits for from the other side (its first wait), or null. */
  /** #2282: `judgment_refresh_unavailable`, why the tasks were made without reading the source again. */
  card: {
    steps: WorkStep[];
    more: number;
    progress: { done: number; total: number };
    waiting: string | null;
    judgment_refresh_unavailable?: { reason: 'not_mail' | 'reconnect' | 'mail_gone' | 'temporary'; plan_read: boolean } | null;
  } | null;
  candidate_id: string | null;
  completion_id: string | null;
  /** #1929 S4: a done goal's "reopen?" after a follow-up came. */
  reopen_id?: string | null;
  /** #2985: a goal that only waited for a reply, completed when the reply came; 「戻す」 reopens it. */
  reply_wait_done?: boolean;
  close_proposal_subtask_id: string | null;
  release_hold_event_id?: string | null;
  /** #2732: 「担当ですか」 for one condition whose actor Jev could not tell. */
  actor_question?: { condition_ref: string; teammates: Array<{ user_id: string; name: string }> } | null;
  /** #1929 S4: a new thread message waiting to be told which goal it continues; it has no goal id. */
  goal_link?: WorkGoalLink | null;
  /** Owner 2026-10-28: on a goal's first judgment row, what follows once it is answered. */
  after?: WorkAfter | null;
  source: { type: string | null; url: string | null } | null;
}

/** The wait on the other side or the AI's next task, after the user's judgment. */
export interface WorkAfter {
  on: 'ai' | 'person';
  title: string;
  who: 'counterparty' | 'someone_else' | null;
  name: string | null;
  more: number;
}

export type WorkWhyNowReason =
  | { kind: 'deadline'; state: 'overdue' | 'due_soon' | 'later'; day: string; time: string | null; provisional: boolean }
  | { kind: 'urgent'; basis: 'calendar_invitation' | 'message'; confidence: 'high' | 'medium' | 'low'; read_at: string | null }
  | { kind: 'waiting'; on: 'counterparty' | 'someone_else' | 'ai'; name: string | null; since: string | null }
  | ({ kind: 'clock_check' } & WorkClockCheck);

/** #3182: what the latest morning or evening check found for the goal. */
export interface WorkClockCheck {
  reason: 'wait_overdue' | 'hold_expiring' | 'deadline_near' | 'event_ended' | 'provisional_deadline';
  slot: 'am' | 'pm';
  checked_at: string;
  day: string | null;
  at: string | null;
  title: string | null;
}

function clockInstantText(value: string | null, lang: Lang): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(lang === 'en' ? 'en-US' : 'ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function clockDayText(value: string | null, lang: Lang): string {
  if (!value) return '';
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? shortDay(value) : clockInstantText(value, lang);
}

/**
 * #3182: 「朝の確認：相手の期日（10/23）を過ぎました」, why the latest check
 * brought the row's goal up. One line per check; several reasons are joined.
 */
export function clockCheckText(item: Pick<WorkItem, 'why_now'>, lang: Lang): string {
  const checks = (item.why_now ?? []).flatMap((reason) => reason.kind === 'clock_check' ? [reason] : []);
  if (checks.length === 0) return '';
  const en = lang === 'en';
  const parts = checks.map((check) => {
    const day = clockDayText(check.day, lang);
    const at = clockInstantText(check.at, lang);
    const title = check.title ?? '';
    switch (check.reason) {
      case 'wait_overdue':
        return en ? `the reply was due${day ? ` ${day}` : ''}` : `相手の期日${day ? `（${day}）` : ''}を過ぎました`;
      case 'hold_expiring':
        return en ? `the hold${title ? ` "${title}"` : ''} expires${at ? ` ${at}` : ''}` : `仮押さえ${title ? `「${title}」` : ''}の期限${at ? ` ${at}` : 'が近づいています'}`;
      case 'deadline_near':
        return en ? `the deadline is near${day ? ` (${day})` : ''}` : `期限が近づいています${day ? `（${day}）` : ''}`;
      case 'event_ended':
        return en ? `the event${title ? ` "${title}"` : ''} has ended` : `予定${title ? `「${title}」` : ''}が終わりました`;
      case 'provisional_deadline':
        // The row's note already asks about the date, so the check is named only.
        return '';
    }
  }).filter((part) => part !== '');
  const label = checks[0]!.slot === 'am' ? (en ? 'Morning check' : '朝の確認') : (en ? 'Evening check' : '夕方の確認');
  return parts.length === 0 ? label : `${label}${en ? ': ' : '：'}${parts.join(en ? '; ' : '、')}`;
}

/**
 * #3184: 「急ぎ（招待への出欠の返事）」, why a row reads as urgent. The deadline and the
 * wait already show on the row (due, waiting line), so only the urgency is worded.
 */
export function urgentText(item: Pick<WorkItem, 'why_now'>, lang: Lang): string {
  const urgent = urgentReason(item);
  if (!urgent) return '';
  const en = lang === 'en';
  if (urgent.basis === 'calendar_invitation') return en ? 'Urgent (reply to the invitation)' : '急ぎ（招待への出欠の返事）';
  return en ? 'Urgent (from what came in)' : '急ぎ（届いた内容から）';
}

export interface WorkGoalLink {
  hold_id: string;
  reason: 'needs_principal' | 'unavailable';
  provider: 'gmail' | 'slack';
  sent_at: string | null;
  message_url: string;
  /** The goals it may continue that are still open; empty when they are done or gone. */
  candidate_goals: Array<{ goal_id: string; title: string }>;
  /** The done goals it may have continued; they only name the thread. */
  done_goals?: Array<{ goal_id: string; title: string }>;
}

export interface WorkKindSummary {
  count: number;
  overdue: number;
  low?: number;
  person?: number;
  ai?: number;
}

export interface WorkList {
  today: string;
  next_working_day: string;
  timezone: string;
  sources: { goals: 'ok' | 'failed'; candidates: 'ok' | 'failed'; holds?: 'ok' | 'failed' };
  summary: { task: WorkKindSummary; judgment: WorkKindSummary; waiting: WorkKindSummary };
  items: WorkItem[];
}

export const WORK_KINDS: WorkKind[] = ['task', 'judgment', 'waiting'];
export const WORK_GROUPS: WorkGroup[] = ['confirm', 'choose', 'result', 'next'];

export const WORK_KIND_ICONS: Record<WorkKind, string> = {
  task: 'task_alt',
  judgment: 'alt_route',
  waiting: 'hourglass_top',
};

/**
 * The list's words. The task rows are one card per Goal, so they are ゴール;
 * タスク is used only inside a goal's detail. ゴール and 判断 together are
 * やること, which heads their tiles (Yoshie 2026-10-28).
 */
export function kindLabel(kind: WorkKind, lang: Lang) {
  if (kind === 'task') return lang === 'en' ? 'Goals' : 'ゴール';
  if (kind === 'judgment') return lang === 'en' ? 'Decisions' : '判断';
  return lang === 'en' ? 'Waiting' : '対応待ち';
}

export function sectionLabel(kind: WorkKind, lang: Lang) {
  return kindLabel(kind, lang);
}

export function groupLabel(group: WorkGroup, lang: Lang) {
  if (group === 'confirm') return lang === 'en' ? 'Confirm' : '完了・ゴールの確認';
  if (group === 'choose') return lang === 'en' ? 'Decide' : '判断';
  if (group === 'result') return lang === 'en' ? "Check the AI's work" : 'AIがしたことの確認';
  return lang === 'en' ? 'Decide what comes next' : '次にやることを決める';
}

/** 人 1・AI 2: who the waiting rows wait on. */
export function waitingSplit(summary: WorkKindSummary, lang: Lang) {
  return lang === 'en'
    ? `People ${summary.person ?? 0} · AI ${summary.ai ?? 0}`
    : `人 ${summary.person ?? 0}・AI ${summary.ai ?? 0}`;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function sentAtText(value: unknown, lang: Lang): string {
  if (typeof value !== 'string' || !value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const shown = date.toLocaleString(lang === 'en' ? 'en-US' : 'ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  return lang === 'en' ? `Received ${shown}` : `${shown} 受信`;
}

/**
 * Owner 2026-10-29: a Goal left to the AI waits as one row. Its heading is the
 * Goal, so it is found by the Goal's name; the AI's task goes under it.
 */
export function isAiWaitRow(item: Pick<WorkItem, 'waiting' | 'executor' | 'goal_title' | 'card'>): boolean {
  return item.waiting?.on === 'ai' && item.executor === 'ai' && !item.card && !!item.goal_title;
}

/**
 * The row's title. A held thread message has none of its own: it asks which
 * goal it continues, or, when those goals are done or gone, whether to make a
 * new goal of it, naming the done goal so the thread can be told apart.
 */
export function rowTitle(item: Pick<WorkItem, 'title' | 'goal_link' | 'waiting' | 'executor' | 'goal_title' | 'card'>, lang: Lang): string {
  if (isAiWaitRow(item)) return item.goal_title;
  const link = item.goal_link;
  if (!link) return item.title;
  const en = lang === 'en';
  const message = link.provider === 'slack' ? (en ? 'message' : 'メッセージ') : (en ? 'mail' : 'メール');
  if (link.candidate_goals.length === 0) {
    const done = link.done_goals ?? [];
    if (done.length > 0) {
      const more = done.length - 1;
      if (en) return `A new ${message} came in the thread of "${done[0].title}"${more > 0 ? ` and ${more} more` : ''} (done). Make it a new goal?`;
      return `『${done[0].title}』${more > 0 ? `ほか${more}件` : ''}（完了済み）のスレッドに新しい${message}が届きました。新しいゴールにしますか？`;
    }
    return en
      ? `A new ${message} came in a thread whose goal is already done. Make it a new goal?`
      : `終わったゴールのスレッドに新しい${message}が届きました。新しいゴールにしますか？`;
  }
  return en ? `Which goal does this new ${message} continue?` : `新しい${message}は、どのゴールの続きですか？`;
}

const WAIT_WHAT_MAX = 40;

/** 「今日から」「昨日から」「3日前から」: how long a wait has run, in whole local days. */
function waitingSinceText(since: string | null, lang: Lang, now: Date = new Date()): string {
  if (!since) return '';
  const start = new Date(since);
  if (Number.isNaN(start.getTime())) return '';
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.max(0, Math.round((day(now) - day(start)) / 86_400_000));
  if (lang === 'en') return days === 0 ? 'since today' : days === 1 ? 'since yesterday' : `${days} days`;
  return days === 0 ? '今日から' : days === 1 ? '昨日から' : `${days}日前から`;
}

/**
 * #2970: 「Horiさん｜ランチの候補日への返事｜3日前から」, the same words as the
 * extension (chrome-extension/src/js/data/work-items.ts waitingText).
 */
export function waitingLine(p: Record<string, unknown>, lang: Lang, now: Date = new Date()): string {
  const en = lang === 'en';
  const name = text(p.name);
  const who = p.who === 'someone_else'
    ? (en ? 'Someone else' : 'ほかの方')
    : name ? (en || /(様|さん|氏)$/u.test(name) ? name : `${name}さん`) : (en ? 'Them' : '相手');
  const whatRaw = text(p.what) ?? '';
  const what = whatRaw.length > WAIT_WHAT_MAX ? `${whatRaw.slice(0, WAIT_WHAT_MAX)}…` : whatRaw;
  // Without what is waited for, the person carries 「の対応待ち」.
  const lead = what ? who : en ? `Waiting on ${who === 'Them' ? 'them' : who}` : `${who}の対応待ち`;
  return [lead, what, waitingSinceText(text(p.since), lang, now)].filter(Boolean).join(en ? ' · ' : '｜');
}

/** Who has a turn on a Goal card's timeline (the detail's actor badges: あなた / AI / the person). */
export type CardTurnActor = 'you' | 'ai' | 'person';

/** One turn on a Goal card's timeline: a step of the user's or the AI's, or a wait on a person. */
export interface CardTurn {
  actor: CardTurnActor;
  /** The badge: あなた, AI, or whom the Goal waits on (相手 when unnamed). */
  who: string;
  title: string;
  /** The open step, when the turn is one (its title opens it; an AI step says where it is). */
  step: WorkStep | null;
  /** On a wait: since when, in words (「3日前から」). */
  since: string;
}

function personWho(p: Record<string, unknown>, lang: Lang): string {
  const en = lang === 'en';
  if (p.who === 'someone_else') return en ? 'Someone else' : 'ほかの方';
  const name = text(p.name);
  return name ? (en || /(様|さん|氏)$/u.test(name) ? name : `${name}さん`) : (en ? 'Them' : '相手');
}

/**
 * The goal card (owner 2026-10-25, 案C-1): whose turn is next and whose comes
 * after, in order, with how many more. On the other side's turn the wait
 * comes first; a wait the plan holds while it is the user's turn comes after
 * the user's first step. The rest stays in the detail beside the list.
 */
export function cardTimeline(item: Pick<WorkItem, 'kind' | 'note' | 'card'>, lang: Lang, now: Date = new Date()): { turns: CardTurn[]; more: number } {
  const card = item.card;
  if (!card) return { turns: [], more: 0 };
  const en = lang === 'en';
  const steps: CardTurn[] = card.steps.filter((step) => !step.closed).map((step) => step.executor === 'ai'
    ? { actor: 'ai', who: 'AI', title: step.title, step, since: '' }
    : { actor: 'you', who: en ? 'You' : 'あなた', title: step.title, step, since: '' });
  const turns = [...steps];
  if (item.kind === 'waiting' && item.note?.code === 'waiting_on') {
    const p = item.note.params || {};
    const whatRaw = text(p.what) ?? '';
    turns.unshift({
      actor: 'person',
      who: personWho(p, lang),
      title: (whatRaw.length > WAIT_WHAT_MAX ? `${whatRaw.slice(0, WAIT_WHAT_MAX)}…` : whatRaw) || (en ? 'Their reply' : '対応待ち'),
      step: null,
      since: waitingSinceText(text(p.since), lang, now),
    });
  } else if (card.waiting != null) {
    turns.splice(Math.min(1, turns.length), 0, {
      actor: 'person', who: en ? 'Them' : '相手', title: card.waiting || (en ? 'Their reply' : '対応待ち'), step: null, since: '',
    });
  }
  return { turns: turns.slice(0, 2), more: Math.max(0, turns.length - 2) + card.more };
}

/**
 * Owner 2026-10-28: what follows the user's judgment, as one turn of the
 * card's timeline (the same badges), with how many more of the AI's tasks.
 */
export function afterTurn(after: WorkAfter | null | undefined, lang: Lang): { turn: CardTurn; more: number } | null {
  if (!after) return null;
  const en = lang === 'en';
  if (after.on === 'ai') return { turn: { actor: 'ai', who: 'AI', title: after.title, step: null, since: '' }, more: after.more };
  const what = after.title.length > WAIT_WHAT_MAX ? `${after.title.slice(0, WAIT_WHAT_MAX)}…` : after.title;
  return {
    turn: { actor: 'person', who: personWho({ who: after.who, name: after.name }, lang), title: what || (en ? 'Their reply' : '対応待ち'), step: null, since: '' },
    more: 0,
  };
}

/** #2282: the short words on the card for a source not read again; the detail says the rest. */
export function refreshUnavailableShort(reason: 'not_mail' | 'reconnect' | 'mail_gone' | 'temporary', lang: Lang): string {
  if (lang === 'en') return reason === 'not_mail' ? 'Source not read again' : reason === 'reconnect' ? 'Gmail not connected; email not read again' : 'Email not read again';
  return reason === 'not_mail' ? '元の内容を読み直せませんでした' : reason === 'reconnect' ? 'Gmail の接続が切れていて読み直せませんでした' : '元のメールを読み直せませんでした';
}

/** The words for a row's note code; the server sends codes, not sentences. */
/** #2745: why a goal candidate is put to the user, as its row and detail say it. */
export interface CandidateReasonView {
  icon: string;
  label: string;
  detail: string;
}

const CANDIDATE_REASONS: Record<string, { icon: string; ja: [string, string]; en: [string, string] }> = {
  solicitation: {
    icon: 'campaign',
    ja: ['案内・勧誘のメールです', '申し込みや参加を勧める案内です。応じるかどうかをお決めください。'],
    en: ['An announcement or offer', 'It invites you to sign up or take part. Decide whether to take it up.'],
  },
  not_addressed: {
    icon: 'group',
    ja: ['あなた宛てではありません', '共有アドレスやメーリングリストで届いたため、あなたの担当かどうか分かりませんでした。'],
    en: ['Not addressed to you', 'It came through a shared address or a mailing list, so it is unclear whether it is yours to handle.'],
  },
  unclear: {
    icon: 'help',
    ja: ['やることがあるか読み切れません', 'あなたが何かする必要があるか、はっきり読み取れませんでした。'],
    en: ['Unclear whether you need to act', 'It was not clear whether there is something for you to do.'],
  },
  context_missing: {
    icon: 'manage_search',
    ja: ['前後の事情が分かりません', 'このメッセージだけでは前後の事情が分からず、やることを決めきれませんでした。'],
    en: ['Missing context', 'This message alone did not say enough to tell what is to be done.'],
  },
  sent_unclear: {
    icon: 'outgoing_mail',
    ja: ['送ったメールに約束があるか読み切れません', 'あなたが送ったメールに、これからすることの約束が含まれるか、はっきり読み取れませんでした。'],
    en: ['Unclear whether your message promised something', 'It was not clear whether the message you sent promises something you will do.'],
  },
  // #3729: every matter found in meeting minutes is put to the user.
  meeting_record: {
    icon: 'description',
    ja: ['議事録から見つけた件です', '会議の議事録にあった、あなたがやることか、相手があなたに約束したことです。追いかけるかどうかをお決めください。'],
    en: ['Found in meeting minutes', 'The minutes of a meeting say you will do this, or someone promised it to you. Decide whether to track it.'],
  },
};

/** The reason a goal candidate carries (null for one made before #2745, or for any other row). */
export function candidateReasonView(note: WorkNote | null, lang: Lang): CandidateReasonView | null {
  if (note?.code !== 'goal_candidate' || typeof note.params.reason !== 'string') return null;
  const reason = CANDIDATE_REASONS[note.params.reason];
  if (!reason) return null;
  const [label, detail] = lang === 'en' ? reason.en : reason.ja;
  return { icon: reason.icon, label, detail };
}

/** #2745: what arrived for a goal candidate: who sent it, the subject, the Slack channel, and when; null when not kept. */
export function candidateOriginView(note: WorkNote | null, lang: Lang): { from: string; subject: string; channel: string; at: string } | null {
  if (note?.code !== 'goal_candidate') return null;
  const origin = note.params.origin as Record<string, unknown> | null | undefined;
  if (!origin || typeof origin !== 'object') return null;
  const from = typeof origin.from === 'string' ? origin.from : '';
  const channel = typeof origin.channel === 'string' ? `#${origin.channel.replace(/^#/, '')}` : '';
  const subject = typeof origin.subject === 'string' ? origin.subject : '';
  const at = sentAtText(origin.received_at, lang);
  return from || subject || channel || at ? { from, subject, channel, at } : null;
}

/** What a goal candidate would achieve once it is a goal (#2745). */
export function candidateConditions(note: WorkNote | null): string[] {
  if (note?.code !== 'goal_candidate' || !Array.isArray(note.params.conditions)) return [];
  return note.params.conditions.filter((what): what is string => typeof what === 'string' && what.trim() !== '');
}

export function noteText(note: WorkNote | null, lang: Lang): string {
  if (!note) return '';
  const p = note.params || {};
  const en = lang === 'en';
  switch (note.code) {
    case 'more':
      return en ? `${p.count} more` : `ほか${p.count}件`;
    case 'waiting_on':
      return waitingLine(p, lang);
    case 'check_result':
      return p.ai ? (en ? "Check the AI's result" : 'AIの結果を確認') : (en ? 'Check the result' : '結果を確認');
    case 'confirm_goal':
      return en ? 'Confirm this goal' : 'ゴールの確認';
    case 'review_change_proposal':
      return en ? 'Review the proposed change' : '変更の提案を確認';
    case 'review_unavailable':
      return en ? 'Could not review; please check' : '見直せなかったので確認';
    case 'completion_candidate':
      return text(p.reason_summary) ?? (en ? 'Looks done; confirm to complete' : '完了したようです。確認してください');
    case 'reopen_candidate':
      return en ? 'A follow-up came after this goal was done. Reopen it?' : '完了後に続報が届きました。再開しますか？';
    case 'reply_wait_done': {
      // #2985: what the reply said, and the goal candidate made from it.
      const line = text(p.line);
      const next = p.next_goal && typeof p.next_goal === 'object' ? text((p.next_goal as { title?: unknown }).title) : null;
      if (en) return ['The reply came, so this was marked done.', line, next ? `Next: "${next}".` : null].filter(Boolean).join(' ');
      return `返事が届いたので完了にしました。${line ?? ''}${next ? `次の件は「${next}」です。` : ''}`;
    }
    case 'close_proposal': {
      const from = text(p.from);
      if (en) {
        const who = from ? `${from}'s reply` : 'The latest reply';
        return p.reason === 'not_needed' ? `${who} suggests this is no longer needed` : `${who} suggests this is done`;
      }
      const who = from ? `${from}さんの返信` : '最新の返信';
      return p.reason === 'not_needed' ? `${who}から、不要になったようです` : `${who}から、済んだようです`;
    }
    case 'provisional_deadline': {
      // #3064 (owner 2026-10-26): ask for the date, not whether to go on.
      const day = typeof p.day === 'string' ? weekdayDay(p.day, lang) : '';
      const next = typeof p.suggested_day === 'string' ? weekdayDay(p.suggested_day, lang) : '';
      if (p.passed) return en ? `The tentative deadline ${day} has passed. Make it ${next}?` : `仮で${day}にしていた期限が過ぎました。${next}までにしますか`;
      return en ? `The deadline is tentatively ${day}. Keep that date?` : `期限は仮で${day}にしています。この日にしますか`;
    }
    case 'confirm_actor': {
      const condition = text(p.condition);
      if (en) return condition ? `Who carries out "${condition}"?` : 'Who carries this out?';
      return condition ? `「${condition}」は誰が進めますか` : '誰が進めますか';
    }
    case 'goal_candidate':
      return text(p.summary) ?? (en ? 'Proposed as a goal' : 'ゴールの候補');
    case 'goal_link_hold': {
      const sent = sentAtText(p.sent_at, lang);
      const unread = p.reason === 'unavailable' ? (en ? 'Zenmetry could not tell' : '自動では判定できませんでした') : '';
      return [sent, unread].filter(Boolean).join(' · ');
    }
    case 'no_tasks_left':
      return en ? 'Nothing left to do' : '残りのやることがありません';
    case 'tasks_failed':
      return en ? 'Could not work out what to do' : 'やることを作れませんでした';
    default:
      return '';
  }
}

const ROUTE_VERBS: Record<string, { ja: string; en: string }> = {
  read: { ja: '調べます', en: 'look it up' },
  draft: { ja: '下書きを作ります', en: 'draft it' },
  send: { ja: '送ります', en: 'send it' },
  schedule: { ja: '予定を入れます', en: 'schedule it' },
  respond_invite: { ja: '出欠を返します', en: 'reply to the invitation' },
  make: { ja: '作ります', en: 'make it' },
  register: { ja: '登録します', en: 'register it' },
  apply: { ja: '申請します', en: 'apply' },
  update: { ja: '直します', en: 'update it' },
  organize: { ja: '整理します', en: 'organize it' },
  delete: { ja: '消します', en: 'delete it' },
  approve: { ja: '承認します', en: 'approve it' },
  site_procedure: { ja: '手続きします', en: 'complete the procedure' },
  pay: { ja: '支払います', en: 'pay' },
};

/**
 * 「Gmail で下書きを作ります」: where the AI works on this row. Through the
 * browser it says so, since the AI opens the service there.
 */
export function aiRouteText(route: WorkAiRoute | null | undefined, lang: Lang): string {
  if (!route) return '';
  const service = serviceLabel(route.where, lang);
  const verb = ROUTE_VERBS[route.how];
  const browser = route.tool_service === 'browser' && route.where !== 'browser';
  if (lang === 'en') {
    const action = verb?.en ?? 'work on it';
    return browser ? `In ${service} (browser), AI will ${action}` : `In ${service}, AI will ${action}`;
  }
  const action = verb?.ja ?? '進めます';
  return browser ? `ブラウザで${service}を開いて${action}` : `${service}で${action}`;
}

/** Where a task the review closed came from, and what it decided. */
export function closedText(closed: NonNullable<WorkStep['closed']>, lang: Lang) {
  // #3692: closed with no message behind it.
  if (closed.basis === 'condition_met') return lang === 'en' ? 'No longer needed: its condition is met' : '条件を満たしたので不要にしました';
  if (closed.basis === 'time_passed') return lang === 'en' ? 'No longer needed: its time has passed' : '時間が過ぎたので不要にしました';
  const done = closed.reason === 'done';
  if (lang === 'en') {
    const source = closed.sent_by_principal ? 'your message' : closed.from ? `${closed.from}'s reply` : 'the latest reply';
    return done ? `Marked done from ${source}` : `Marked not needed from ${source}`;
  }
  const source = closed.sent_by_principal ? 'あなたの送ったメッセージ' : closed.from ? `${closed.from}さんの返信` : '最新の返信';
  return done ? `${source}から完了にしました` : `${source}から不要と判断しました`;
}

export function nextDay(day: string) {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

function shortDay(day: string) {
  const [, m, d] = day.split('-').map(Number);
  return `${m}/${d}`;
}

/** 「10/30(金)」, `Fri 10/30` in English. */
/**
 * #3064 (owner 2026-10-26): passed provisional deadlines are asked together,
 * 「仮の期限が過ぎたゴールが78件あります。まとめて10/30(金)にしますか」, and
 * each one can still be answered on its own once the bundle is opened. The
 * rows stay one per Goal in the list (and in MCP `list_work`); only the Web
 * view folds them. Returns null when fewer than two passed.
 */
export function passedProvisionalBundle(rows: WorkItem[]): { items: WorkItem[]; day: string } | null {
  const items = rows.filter((item) => item.note?.code === 'provisional_deadline' && item.note.params.passed === true);
  const day = items[0]?.note?.params.suggested_day;
  if (items.length < 2 || typeof day !== 'string') return null;
  return { items, day };
}

export function provisionalBundleText(count: number, day: string, lang: Lang) {
  const next = weekdayDay(day, lang);
  return lang === 'en'
    ? { title: `${count} goals are past their tentative deadline`, ask: `Make them all ${next}?`, all: `Make all ${next}`, open: 'See one by one', close: 'Close' }
    : { title: `仮の期限が過ぎたゴールが${count}件あります`, ask: `まとめて${next}までにしますか`, all: `まとめて${next}にする`, open: '1件ずつ見る', close: '閉じる' };
}

export function weekdayDay(day: string, lang: Lang) {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  if (Number.isNaN(weekday)) return day;
  return lang === 'en' ? `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][weekday]} ${shortDay(day)}` : `${shortDay(day)}(${'日月火水木金土'[weekday]})`;
}

/**
 * The date column: the time today, 今日 / 明日, M/D, or a dash. A deadline
 * that was not read (#3064) is said to be tentative to screen readers too;
 * the 「仮」 tag itself is drawn by the row.
 */
export function dueLabel(item: Pick<WorkItem, 'due' | 'overdue'>, today: string, lang: Lang) {
  const label = dueLabelOf(item, today, lang);
  if (!item.due?.provisional) return label;
  return { ...label, aria: lang === 'en' ? `Tentative deadline, ${label.aria}` : `仮の期限 ${label.aria}` };
}

function dueLabelOf(item: Pick<WorkItem, 'due' | 'overdue'>, today: string, lang: Lang) {
  const en = lang === 'en';
  if (!item.due) return { text: '—', aria: en ? 'No deadline' : '期限なし' };
  const { day, time } = item.due;
  if (item.overdue) {
    const shown = shortDay(day);
    return { text: shown, aria: en ? `Overdue, due ${shown}` : `期限切れ ${shown}` };
  }
  if (day === today) {
    const shown = time || (en ? 'Today' : '今日');
    return { text: shown, aria: en ? `Due today ${time || ''}`.trim() : `今日 ${time || ''}`.trim() };
  }
  if (day === nextDay(today)) {
    const shown = en ? 'Tomorrow' : '明日';
    return { text: shown, aria: shown };
  }
  const shown = shortDay(day);
  return { text: shown, aria: shown };
}

export type WorkDay = 'today' | 'tomorrow' | 'day_after' | 'later' | 'none';
const WORK_DAYS: WorkDay[] = ['today', 'tomorrow', 'day_after', 'later', 'none'];

/**
 * やること split by when it is due: by today (overdue included), tomorrow,
 * the day after, later, and undated. Rows keep the server's order inside a
 * day; empty days are left out.
 */
export function dayGroups<T = WorkItem>(
  items: T[],
  today: string,
  dueOf: (row: T) => WorkItem['due'] = (row) => (row as unknown as WorkItem).due,
): Array<{ day: WorkDay; rows: T[] }> {
  const tomorrow = nextDay(today);
  const dayAfter = nextDay(tomorrow);
  const dayOf = (row: T): WorkDay => {
    const due = dueOf(row);
    if (!due) return 'none';
    if (due.day <= today) return 'today';
    if (due.day === tomorrow) return 'tomorrow';
    if (due.day === dayAfter) return 'day_after';
    return 'later';
  };
  return WORK_DAYS
    .map((day) => ({ day, rows: items.filter((row) => dayOf(row) === day) }))
    .filter((group) => group.rows.length > 0);
}

export function dayLabel(day: WorkDay, lang: Lang) {
  const en = lang === 'en';
  return {
    today: en ? 'By today' : '今日まで',
    tomorrow: en ? 'Tomorrow' : '明日',
    day_after: en ? 'Day after tomorrow' : '明後日',
    later: en ? 'Later' : 'それ以降',
    none: en ? 'No date' : '期日なし',
  }[day];
}

/**
 * Why a goal or judgment row goes to 優先 at the top of the list (Yoshie
 * 2026-10-28): a confirmed deadline passed, a confirmed deadline by the next
 * working day, or what came in reads as urgent. A provisional deadline that
 * passed is a date the list guessed, so it stays in its own section. Waiting
 * rows are the other side's turn and stay apart; candidates, held messages
 * and the morning check's provisional rows keep their own places.
 */
export type WorkFirstReason = 'overdue' | 'due_soon' | 'urgent';
const FIRST_ORDER: WorkFirstReason[] = ['overdue', 'due_soon', 'urgent'];

/**
 * Yoshie 2026-10-29: an urgency the reading itself was unsure of (low
 * confidence) does not make a row urgent; it only filled 優先 with meetings
 * weeks away.
 */
function urgentReason(item: Pick<WorkItem, 'why_now'>) {
  const urgent = (item.why_now ?? []).find((reason) => reason.kind === 'urgent');
  return urgent && urgent.kind === 'urgent' && urgent.confidence !== 'low' ? urgent : null;
}

export function firstReason(item: WorkItem, list: Pick<WorkList, 'today' | 'next_working_day'>): WorkFirstReason | null {
  if (isLooseRow(item)) return null;
  const confirmed = item.due && !item.due.provisional ? item.due.day : null;
  if (confirmed && confirmed < list.today) return 'overdue';
  if (confirmed && confirmed <= list.next_working_day) return 'due_soon';
  if (urgentReason(item)) return 'urgent';
  return null;
}

export function firstLabel(lang: Lang) {
  return lang === 'en' ? 'Priority' : '優先';
}

/**
 * Rows that stay rows of their own: the other side's turn (対応待ち), and
 * what is not yet a goal's work or answers for the goal as a whole: goal
 * candidates, completion and reopen proposals, held thread messages, the
 * provisional deadline asks and a reply-wait completed.
 */
export function isLooseRow(item: WorkItem) {
  return item.kind === 'waiting' || isSelectable(item) || !!item.goal_link || !!item.reopen_id || !!item.reply_wait_done
    || item.note?.code === 'provisional_deadline';
}

/**
 * One card per Goal (Yoshie 2026-10-29): a goal's judgments and the AI's
 * work to check sit inside its card, on the line of turns, instead of rows of
 * their own. `lead` is the plan's card when the goal has one, else its first
 * judgment; the card opens it.
 */
export interface WorkGoalCard {
  key: string;
  goal_id: string;
  lead: WorkItem;
  task: WorkItem | null;
  judgments: WorkItem[];
}

export function goalCards(items: WorkItem[]): WorkGoalCard[] {
  const byGoal = new Map<string, { task: WorkItem | null; judgments: WorkItem[] }>();
  for (const item of items) {
    if (isLooseRow(item)) continue;
    const entry = byGoal.get(item.goal_id) ?? { task: null, judgments: [] };
    if (item.kind === 'task' && !entry.task) entry.task = item;
    else entry.judgments.push(item);
    byGoal.set(item.goal_id, entry);
  }
  return [...byGoal.entries()].map(([goalId, { task, judgments }]) => {
    const lead = task ?? judgments[0]!;
    return { key: lead.key, goal_id: goalId, lead, task, judgments };
  });
}

export function cardRows(card: WorkGoalCard) {
  return card.task ? [card.task, ...card.judgments] : card.judgments;
}

/** The card's most pressing reason for 優先, if any. */
export function cardFirstReason(card: WorkGoalCard, list: Pick<WorkList, 'today' | 'next_working_day'>): WorkFirstReason | null {
  const ranks = cardRows(card).map((item) => firstReason(item, list)).filter((reason): reason is WorkFirstReason => reason !== null)
    .map((reason) => FIRST_ORDER.indexOf(reason));
  return ranks.length > 0 ? FIRST_ORDER[Math.min(...ranks)]! : null;
}

/**
 * Where a card is counted and listed: 判断 when it asks the user to decide
 * something, else ゴール; a card with only low-priority judgments folds at
 * the end of 判断 (Yoshie 2026-10-29, the tiles keep their two parts).
 */
export function cardKind(card: WorkGoalCard): 'task' | 'judgment' | 'low' {
  if (card.judgments.some((item) => item.priority === 'high')) return 'judgment';
  return card.task ? 'task' : 'low';
}

export function cardOverdue(card: WorkGoalCard) {
  return cardRows(card).some((item) => item.overdue);
}

/** The row whose date the card shows: the first one overdue, else the plan's (or first judgment's). */
export function cardDueRow(card: WorkGoalCard) {
  return cardRows(card).find((item) => item.overdue) ?? card.lead;
}

/** 優先: the pressing cards, passed deadlines first, then the near ones, then what came in urgent; list order inside each. */
export function firstCards(cards: WorkGoalCard[], list: Pick<WorkList, 'today' | 'next_working_day'>) {
  return cards
    .map((card, index) => ({ card, index, reason: cardFirstReason(card, list) }))
    .filter((entry) => entry.reason !== null)
    .sort((a, b) => FIRST_ORDER.indexOf(a.reason!) - FIRST_ORDER.indexOf(b.reason!) || a.index - b.index)
    .map(({ card }) => card);
}

/** The loose rows of one kind (not in a card), split into the ones shown and the low-priority judgments folded at the end. */
export function sectionRows(items: WorkItem[], kind: WorkKind) {
  const rows = items.filter((item) => item.kind === kind && isLooseRow(item));
  const low = kind === 'judgment' ? rows.filter((item) => item.priority === 'low') : [];
  return { main: rows.filter((item) => !low.includes(item)), low };
}

/**
 * The tiles' numbers, counted in cards (Yoshie 2026-10-29): ゴール and 判断
 * split the cards, 判断 adding the loose judgments (candidates, proposals,
 * held messages); `low` are the folded ones.
 */
export function workCounts(list: WorkList) {
  const cards = goalCards(list.items);
  const loose = sectionRows(list.items, 'judgment');
  const of = (kind: 'task' | 'judgment' | 'low') => cards.filter((card) => cardKind(card) === kind);
  return {
    task: { count: of('task').length, overdue: of('task').filter(cardOverdue).length },
    judgment: {
      count: of('judgment').length + loose.main.length,
      overdue: of('judgment').filter(cardOverdue).length + loose.main.filter((item) => item.overdue).length,
      low: of('low').length + loose.low.length,
    },
  };
}

/** Rows that can be approved or turned down together: goal candidates and completion proposals. */
export function isSelectable(item: WorkItem) {
  return item.candidate_id !== null || item.completion_id !== null;
}

/**
 * The bulk bar's two buttons, worded for what is chosen (#2857): goal
 * candidates become goals, completion candidates complete, and a mix can only
 * be turned down together.
 */
export function bulkLabels(chosen: WorkItem[], lang: Lang): { accept: string | null; dismiss: string } {
  const en = lang === 'en';
  const dismiss = en ? 'Not needed' : 'まとめて不要';
  const goals = chosen.some((item) => item.candidate_id !== null);
  const completions = chosen.some((item) => item.completion_id !== null);
  if (goals && completions) return { accept: null, dismiss };
  if (completions) return { accept: en ? 'Complete all' : 'まとめて完了にする', dismiss };
  return { accept: en ? 'Make goals' : 'まとめてゴールにする', dismiss };
}

/**
 * The rows of the list in a range: 今日 keeps rows due today or earlier, the
 * 優先 rows, and goal candidates and held thread messages, which wait for an
 * answer whatever their date.
 */
export function scopeWorkList(list: WorkList, range: WorkRange): WorkList {
  if (range === 'all') return list;
  // 優先 rows show whatever their date: what came in urgent may be due later.
  const shown = (item: WorkItem) => item.candidate_id !== null || !!item.goal_link || (!!item.due && item.due.day <= list.today) || firstReason(item, list) !== null;
  // A goal's card keeps all its rows when one of them is shown (Yoshie 2026-10-29).
  const cardGoals = new Set(list.items.filter((item) => !isLooseRow(item) && shown(item)).map((item) => item.goal_id));
  const items = list.items.filter((item) => (!isLooseRow(item) && cardGoals.has(item.goal_id)) || shown(item));
  return { ...list, items, summary: summarizeRows(items) };
}

/**
 * The menu item a search result opens under: 今日 when the goal has a row
 * there, else すべて, so the goal is in the list beside its detail.
 */
export function searchResultRange(list: WorkList | null, goalId: string): WorkRange {
  if (!list) return 'today';
  return scopeWorkList(list, 'today').items.some((item) => item.goal_id === goalId) ? 'today' : 'all';
}

/**
 * The counts of the rows shown (#2856); a goal's card is one row of やること,
 * dated by its first task.
 */
export function summarizeRows(items: WorkItem[]): WorkList['summary'] {
  const of = (kind: WorkKind) => items.filter((item) => item.kind === kind);
  const judgments = of('judgment');
  const waiting = of('waiting');
  return {
    task: { count: of('task').length, overdue: of('task').filter((item) => item.overdue).length },
    judgment: {
      count: judgments.filter((item) => item.priority === 'high').length,
      overdue: judgments.filter((item) => item.overdue).length,
      low: judgments.filter((item) => item.priority === 'low').length,
    },
    waiting: {
      count: waiting.length,
      overdue: waiting.filter((item) => item.overdue).length,
      person: waiting.filter((item) => item.waiting?.on === 'person').length,
      ai: waiting.filter((item) => item.waiting?.on === 'ai').length,
    },
  };
}

/**
 * やること: the Goals and the judgments not folded away, the total heading
 * their two tiles (Yoshie 2026-10-28). The menu carries no count.
 */
export function workCount(list: WorkList | null) {
  if (!list) return 0;
  const counts = workCounts(list);
  return counts.task.count + counts.judgment.count;
}
